import useUsers from "src/api/hooks/useUsers";
import useGroups from "src/api/hooks/useGroups";
import SearchInput from "src/components/SearchInput";
import { useEffect, useState } from "react";
import { DataCard } from "src/components/DataCard";
import { Modal } from "src/components/Modal";
import { Button } from "src/components/Buttons/Button";
import { UserResponse } from "src/types/UserResponse";
import { lang } from "src/language/Strings";
import { ShowToast } from "src/components/Toast";
import { useMutation } from "@tanstack/react-query";
import { portalApiClient } from "src/api/apiClient";
import { apiUrls } from "src/consts/ApiUrls";
import { queryKeys } from "src/api/queryKeys";
import { queryClient } from "src/main";
import { useForm } from "src/api/hooks/useForm";
import { useParams } from "react-router-dom";
import { ItemDescription } from "src/components/ItemDescription";
import { GroupResponse } from "src/types/GroupResponse";
import useChangeTitle from "src/api/hooks/useChangeTitle";

export function GroupsPage() {
  const { data: users } = useUsers();
  const { data: groups, invalidate } = useGroups();
  const [searchText, setSearchText] = useState("");
  const { companyId } = useParams();
  const [selectedGroup, setSelectedGroup] = useState<GroupResponse | null>(
    null
  );
  useChangeTitle(lang.groups);
  useEffect(() => {
    const plusElementHandler = () => {
      setSelectedGroup({
        id: 0,
        name: "",
        companyId: Number(companyId),
        costCenter: "",
        location: "",
      });
    };
    const plusElement = document.getElementById("plus-icon-add");
    if (plusElement) {
      plusElement.addEventListener("click", plusElementHandler);
      return () => {
        plusElement.removeEventListener("click", plusElementHandler);
      };
    }
  }, []);

  return (
    <div className="flex flex-col h-full relative w-full ">
      <div className="flex flex-row w-full p-2">
        <div className="h-full w-full overflow-y-auto">
          <SearchInput onChange={setSearchText} />
          {groups
            ?.filter((group) =>
              `${group.name.toLowerCase()} ${group.location.toLowerCase()}`.includes(
                searchText.toLowerCase()
              )
            )
            .map((group) => (
              <DataCard
                key={"group-" + group.id}
                id={group.id.toString()}
                imageUrl={""}
                header={`${group.name}`}
                firstRowInput={group.costCenter}
                secondRowInput={group.location}
                thirdRowInput={lang.numberOfUsers(
                  users?.filter((user) =>
                    user?.groups?.find((g) => g.id === group.id)
                  ).length || 0
                )}
                onClick={() => {
                  setSelectedGroup(group);
                }}
                isSelected={false}
              />
            ))}
        </div>
      </div>
      {selectedGroup && users ? (
        <GroupModal
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          users={users}
          invalidate={invalidate}
        />
      ) : null}
    </div>
  );
}

function GroupModal({
  selectedGroup,
  setSelectedGroup,
  users,
  invalidate,
}: {
  selectedGroup: GroupResponse | null;
  setSelectedGroup: (group: GroupResponse | null) => void;
  users: UserResponse[];
  invalidate: () => void;
}) {
  const { companyId } = useParams();
  const { Form, formData } = useForm({
    name: selectedGroup?.name || "",
    costCenter: selectedGroup?.costCenter || "",
    location: selectedGroup?.location || "",
    companyId: Number(selectedGroup?.companyId) || Number(companyId),
    id: selectedGroup?.id || undefined,
    userIds: users
      .filter((user) => user?.groups?.find((g) => g.id === selectedGroup?.id))
      ?.map((user) => user.id),
  });

  const createMutation = useMutation({
    mutationFn: async (group: GroupResponse) => {
      if (group.id) {
        const response = await portalApiClient.put(apiUrls.groups, group);
        return response.data;
      }
      const response = await portalApiClient.post(apiUrls.groups, group);
      return response.data;
    },
    onSuccess: async () => {
      invalidate();
      setSelectedGroup(null);
      const userIds: number[] = [];
      const userIdElements = document.getElementsByName("userIds");
      (userIdElements as unknown as HTMLInputElement[]).forEach(
        (el: HTMLInputElement) => {
          if (el.checked) {
            const num = Number(el.id.split("-")[1]);
            userIds.push(num);
          }
        }
      );
      await portalApiClient.put(apiUrls.mapUsersToGroups, {
        groupId: selectedGroup?.id,
        userIds: userIds,
      });

      ShowToast(
        selectedGroup?.id === undefined
          ? lang.createGroupSuccess
          : lang.saveGroupSuccess,
        "success",
        3000
      );
    },
    onError: () => {
      ShowToast(lang.saveGroupError, "error", 3000);
    },
    mutationKey: ["groupMutation", ...queryKeys.groups(companyId)],
  });
  return (
    selectedGroup && (
      <Modal onOutsideClick={() => setSelectedGroup(null)}>
        <h1>
          {selectedGroup.name} {selectedGroup.costCenter}
        </h1>
        <Form id="groupForm">
          <ItemDescription label={lang.group}>
            <Form.Input name="name" defaultValue={formData.name} />
          </ItemDescription>
          <ItemDescription label={lang.costCenter}>
            <Form.Input name="costCenter" defaultValue={formData.costCenter} />
          </ItemDescription>
          <ItemDescription label={lang.location}>
            <Form.Input name="location" defaultValue={formData.location} />
          </ItemDescription>
          <ItemDescription
            label={lang.users}
            className="flex flex-col w-full p-4"
            childrenOnNextLine
          >
            <div className="flex flex-col gap-2 max-h-40 overflow-y-auto text-sm">
              {users.map((user) => (
                <div className="flex flex-row gap-2" key={`user-${user.id}`}>
                  <input
                    type="checkbox"
                    name="userIds"
                    id={`user-${user.id}`}
                    defaultChecked={
                      formData.userIds.includes(user.id) ? true : false
                    }
                  />
                  <label key={`user-${user.id}`}>{user.username}</label>
                </div>
              ))}
            </div>
          </ItemDescription>
        </Form>
        <div className="flex flex-row gap-2 items-center align-middle justify-center">
          <Button
            onClick={() => setSelectedGroup(null)}
            title={lang.close}
            buttonType="button"
          />
          <Button
            title={selectedGroup?.id === undefined ? lang.create : lang.save}
            buttonType="submit"
            onClick={() => {
              createMutation.mutate(formData);
            }}
            formId="groupForm"
          />
        </div>
      </Modal>
    )
  );
}
